/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type {
    GroupByType,
    SwingTimingData,
} from 'leaderboard/scripts/swing-path/types/globals'
import { color as d3Color } from 'd3'
import React, { CSSProperties, MouseEvent } from 'react'
import getShortPitchName from '../utils/get-short-pitch-name.ts'
import {
    getPitchTypeColor,
    getPitchTypeColorGrouping,
} from '../utils/pitch-type-colors'
import getImageUrl from './utils/get-image-url.ts'
import getPitcherType from './utils/get-pitcher-type.ts'
import type { GroupByLogic } from './utils/do-group-by-logic.ts'
import getDistributionColor from './utils/get-distribution-color.ts'
import { getKeyFromRow } from './Wrapper.tsx'
import { getPitchNameGrouping } from '../utils/pitch-names'
import type { PitchTypeColorMode } from './utils/can-use-pitch-type-colors.ts'
import type SwingTimingChart from './swing-timing-chart.ts'

const imageSize = 50

type PlayerCardChartProps = {
    row: SwingTimingData
    groupByLogic: GroupByLogic
    handleSelect: (d: SwingTimingData) => void
    type: GroupByType
    pitchTypeColors: boolean
    pitchTypeColorMode: PitchTypeColorMode
    charts: SwingTimingChart[]
    isLoading?: boolean
}

function getBatterType(row: SwingTimingData) {
    if (row.bat_side_type === 'R') {
        return 'RHH'
    }
    if (row.bat_side_type === 'L') {
        return 'LHH'
    }
    if (row.bat_side_type === 'X') {
        return 'SH'
    }

    return ''
}

function lightenHexColor(hexColor: string, amount = 0.25) {
    const d3ColorValue = d3Color(hexColor)

    if (!d3ColorValue) {
        return hexColor
    }

    return d3ColorValue.brighter(amount).formatRgb()
}

export default function PlayerCardChart({
    row,
    groupByLogic,
    handleSelect,
    type,
    pitchTypeColors,
    pitchTypeColorMode,
    charts,
    isLoading = false,
}: PlayerCardChartProps) {
    const { hasGroupBys, extraGroupBys, groupByTitles, groupByDisplayFns } =
        groupByLogic

    const imgSrc = getImageUrl(type, row)

    const pitchType03 =
        typeof row.api_pitch_type_group03 === 'string'
            ? row.api_pitch_type_group03
            : null
    const pitchType09 =
        typeof row.api_pitch_type_group09 === 'string'
            ? row.api_pitch_type_group09
            : null
    const pitchType =
        pitchTypeColorMode === 'group09' ? pitchType09 : pitchType03

    let color = getDistributionColor(getKeyFromRow(row))

    if (pitchTypeColors && pitchType !== null) {
        color =
            pitchTypeColorMode === 'group09'
                ? getPitchTypeColorGrouping(pitchType, false)
                : getPitchTypeColor(pitchType, false)
    }

    const cardStyle = {
        '--swing-timing-card-border-color': color,
        '--swing-timing-player-card-name-background': lightenHexColor(color),
    } as CSSProperties

    function handleMouseOver() {
        charts.forEach((chart) => {
            chart.setHoveredCurve(getKeyFromRow(row))
        })
    }

    function handleMouseOut() {
        charts.forEach((chart) => {
            chart.setHoveredCurve(null)
        })
    }

    const hiddenGroupBy = type === 'batter' ? 'bat_side' : 'pitch_hand'
    const groupBys = extraGroupBys.filter((key) => key !== hiddenGroupBy)
    const handednessLabel =
        type === 'batter' ? getBatterType(row) : getPitcherType(row)
    const shouldShowHandedness =
        type === 'batter' || row.pitch_hand_type !== 'X'

    function handleRemoveClick(e: MouseEvent<HTMLButtonElement>) {
        e.stopPropagation()
        handleSelect(row)
    }

    const pitchTypeClass = pitchType ? `pitch-type-${pitchType}` : ''
    const loadingClass = isLoading ? `swing-timing-player-card-is-loading` : ''

    return (
        <div
            key={row.uniqueId}
            className={`swing-timing-player-card swing-timing-player-card-legend ${pitchTypeClass} ${loadingClass}`}
            style={cardStyle}
            onMouseEnter={handleMouseOver}
            onMouseLeave={handleMouseOut}
            onFocus={handleMouseOver}
            onBlur={handleMouseOut}
        >
            <button
                type="button"
                className="swing-timing-remove-player"
                aria-label={`Remove ${row.name}`}
                onClick={handleRemoveClick}
                onFocus={handleMouseOver}
                onBlur={handleMouseOut}
            >
                ✖
            </button>
            <img
                alt={row.name}
                className="swing-timing-player-card-image"
                src={imgSrc}
                height={imageSize * 1}
                width={imageSize * 1}
            />
            <div className="swing-timing-player-card-name swing-timing-no-wrap-ellipsis">
                {row.name}
            </div>
            {hasGroupBys && (
                <div className="swing-timing-player-card-group-bys swing-timing-no-wrap-ellipsis">
                    {extraGroupBys
                        .map((key, i) => {
                            if (key === 'api_pitch_type_group03') {
                                return {
                                    key,
                                    label: getShortPitchName(row[key]),
                                }
                            }
                            if (key === 'api_pitch_type_group09') {
                                return {
                                    key,
                                    label: `Group: ${getPitchNameGrouping(
                                        row[key] || ''
                                    )}`,
                                }
                            }

                            const label = groupByTitles[i]

                            const value = groupByDisplayFns[i](row[key], row)

                            return { key, label: `${label}${value}` }
                        })
                        .filter(({ key }) => key !== 'pitch_hand')
                        .filter(({ key }) => key !== hiddenGroupBy)
                        .map(({ key, label }) => (
                            <div
                                className="swing-timing-no-wrap-ellipsis"
                                key={key}
                                style={{
                                    fontSize: `${15 - groupBys.length}px`,
                                }}
                            >
                                {label}
                            </div>
                        ))}
                </div>
            )}
            {shouldShowHandedness && (
                <div className="swing-timing-player-card-handedness">
                    {handednessLabel}
                </div>
            )}
        </div>
    )
}
